import _ from 'lodash';
import ReactQuill from 'react-quill';
import DatePicker from 'react-datepicker';
import Checkbox from '@mui/material/Checkbox';
import "react-datepicker/dist/react-datepicker.css";
import { useSelector, useDispatch } from 'react-redux';
import React, { useState, useEffect, useRef } from 'react';
import 'react-accessible-accordion/dist/fancy-example.css';
import CircleUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import CircleCheckedFilled from '@mui/icons-material/CheckCircleOutline';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { AccordionItemHeading, AccordionItemButton, AccordionItemPanel, AccordionItemState } from 'react-accessible-accordion';

import Actions from './props/Actions';
import { AttachmentList, AttachmentUploader, PendingAttachmentsList } from '../../../components/Attachments';
import { completeActivity } from '../../../store/Activities/index';
import { getMentionModule, setMentionMembers } from '../../../utils/workspaceUtils';
import { IconButton, Tooltip, Chip } from '@mui/material';
import { DataObject as DataIcon } from '@mui/icons-material';

const Task = (props) => {
    const dispatch = useDispatch();
    const { activity, activityData, onUpdateActivity, updateSinglePageReducer, hasExternalData, dataSource, onViewData } = props;

    const { currentWorkspace } = useSelector(state => state['Settings']);

    const [activityState, setActivityState] = useState(activity);
    const [dueDate, setDueDate] = useState(activityState?.['dueDate'] ? new Date(activityState['dueDate']) : null);
    const [openState, setOpenState] = useState({});
    // Local state for description while editing to avoid controlled component conflicts
    const [editingDescription, setEditingDescription] = useState(activityState['description'] || '');

    // Use local state for pending attachments to avoid cross-component sharing
    const [localPendingAttachments, setLocalPendingAttachments] = useState([]);

    useEffect(() => {
        setActivityState(prev => {
            if (prev._id !== activity._id || prev.description !== activity.description || prev.status !== activity.status) {
                return { ...activity };
            }
            return prev;
        });
        if (!openState.isEditingDescription) {
            setEditingDescription(activity['description'] || '');
            editorContentRef.current = activity['description'] || '';
        }
    }, [activity, activity._id, activity.description, activity.status])

    // Focus the quill editor when editing description
    const quillRef = useRef(null);
    // Use a ref to track the latest editor content to avoid stale state issues
    const editorContentRef = useRef(activityState['description'] || '');
    const moreDropdownRef = useRef(null);

    // Update ref and editing state when activity state changes
    useEffect(() => {
        editorContentRef.current = activityState['description'] || '';
        // Only update editing description if not currently editing
        if (!openState.isEditingDescription) {
            setEditingDescription(activityState['description'] || '');
        }
    }, [activityState['description'], openState.isEditingDescription]);

    // Initialize editing description when starting to edit
    useEffect(() => {
        if (openState.isEditingDescription) {
            setEditingDescription(activityState['description'] || '');
        }
    }, [openState.isEditingDescription]);

    useEffect(() => {
        if (openState.isEditingDescription && quillRef.current) {
            const quill = quillRef.current.getEditor();
            const editorElement = quill.root;

            quill.focus();
            // Set cursor to the end of the content
            const length = quill.getLength();
            quill.setSelection(length - 1, 0);

            // Function to sync content from editor to state
            const syncContentToState = () => {
                const content = quill.root.innerHTML;
                // Update ref immediately
                editorContentRef.current = content;
                // Previous code - used requestAnimationFrame but it caused delays with paste
                // requestAnimationFrame(() => {
                //     setEditingDescription(content);
                //     setActivityState(prev => {
                //         if (prev.description !== content) {
                //             return { ...prev, description: content };
                //         }
                //         return prev;
                //     });
                // });
                // Updated: Update state immediately - don't wait for requestAnimationFrame as it causes delays
                setEditingDescription(content);
                setActivityState(prev => {
                    if (prev.description !== content) {
                        return { ...prev, description: content };
                    }
                    return prev;
                });
            };

            // Add custom text-change handler to ensure pasted content is captured immediately
            const handleTextChange = (delta, oldDelta, source) => {
                // source can be 'user', 'api', or 'silent'
                // We want to capture all changes, especially from paste (which is 'user')
                syncContentToState();
            };

            // Listen to text-change event which fires after paste and all content changes
            quill.on('text-change', handleTextChange);

            // Add direct paste event listener on the editor element as a backup
            // Use a flag to prevent onBlur from firing during paste
            let isPasting = false;
            const handlePaste = (e) => {
                isPasting = true;
                // Let Quill handle the paste first, then sync
                setTimeout(() => {
                    syncContentToState();
                    // Reset flag after paste is complete
                    setTimeout(() => {
                        isPasting = false;
                    }, 200);
                }, 0);
            };
            editorElement.addEventListener('paste', handlePaste, true);

            // Prevent blur from closing editor during paste
            const handleBlur = (e) => {
                // Don't auto-save if we're in the middle of a paste operation
                if (isPasting) {
                    e.preventDefault();
                    return;
                }
            };
            editorElement.addEventListener('blur', handleBlur, true);

            // Also listen to selection-change to ensure content is synced
            const handleSelectionChange = () => {
                const content = quill.root.innerHTML;
                if (editorContentRef.current !== content) {
                    syncContentToState();
                }
            };
            quill.on('selection-change', handleSelectionChange);

            // Cleanup listeners when component unmounts or editing stops
            return () => {
                quill.off('text-change', handleTextChange);
                quill.off('selection-change', handleSelectionChange);
                editorElement.removeEventListener('paste', handlePaste, true);
                editorElement.removeEventListener('blur', handleBlur, true);
            };
        }
    }, [openState.isEditingDescription])

    const handleDateChange = (date) => {
        setDueDate(date);
        onUpdateActivity('dueDate', date);
        toggleDropdownAction('datePickerOpen');
    };
    const handleTimeChangeTime = (timePart) => {
        const datePart = dueDate || new Date();
        const combined = new Date(
            datePart.getFullYear(),
            datePart.getMonth(),
            datePart.getDate(),
            timePart.getHours(),
            timePart.getMinutes()
        );
        setDueDate(combined);
        onUpdateActivity('dueDate', combined);
        toggleDropdownAction('timePickerOpen');
    };

    const toggleDropdownAction = (id) => setOpenState((prev) => ({ ...prev, [id]: !prev[id] }));

    // Handle click outside to close More dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openState.showMore &&
                moreDropdownRef.current &&
                !moreDropdownRef.current.contains(event.target)) {
                toggleDropdownAction('showMore');
            }
        };

        if (openState.showMore) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openState.showMore]);

    // Convert plain text URLs to clickable links
    const convertUrlsToLinks = (html) => {
        if (!html) return '';

        // Ensure existing links have proper styling
        let processed = html.replace(/<a\s+([^>]*?)>/gi, (match, attrs) => {
            if (!attrs.includes('class=')) attrs += ' class="description-link"';
            if (!attrs.includes('target=')) attrs += ' target="_blank"';
            if (!attrs.includes('rel=')) attrs += ' rel="noopener noreferrer"';
            return `<a ${attrs}>`;
        });

        try {
            const div = document.createElement('div');
            div.innerHTML = processed;

            const processNode = (node) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    const text = node.textContent;
                    const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+|www\.[^\s<>"{}|\\^`\[\]]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s<>"{}|\\^`\[\]]*)/gi;
                    const matches = [...text.matchAll(urlRegex)];

                    if (matches.length) {
                        const fragment = document.createDocumentFragment();
                        let idx = 0;

                        matches.forEach(m => {
                            if (m.index > idx) fragment.appendChild(document.createTextNode(text.substring(idx, m.index)));
                            const url = m[0];
                            const link = document.createElement('a');
                            Object.assign(link, { href: url.startsWith('http') ? url : `https://${url}`, target: '_blank', rel: 'noopener noreferrer', className: 'description-link', textContent: url });
                            fragment.appendChild(link);
                            idx = m.index + m[0].length;
                        });

                        if (idx < text.length) fragment.appendChild(document.createTextNode(text.substring(idx)));
                        if (node.parentNode) node.parentNode.replaceChild(fragment, node);
                    }
                } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName !== 'A') {
                    Array.from(node.childNodes).forEach(processNode);
                }
            };

            Array.from(div.childNodes).forEach(processNode);
            return div.innerHTML;
        } catch (e) {
            return processed;
        }
    };

    const processHTMLForDisplay = (html) => convertUrlsToLinks(html || '');

    const updateActivity = (field = 'title', value = null) => {
        setOpenState((prev) => ({ ...prev, isEditing: false, isEditingDescription: false }));
        // Previous code - direct access without undefined check
        // onUpdateActivity(field, activityState[field]);
        // onUpdateActivity('attachments', pendingAttachments);

        // Previous code - didn't update state immediately after save
        // const fieldValue = value !== null ? value : (activityState[field] !== undefined ? activityState[field] : '');
        // if (value !== null) {
        //     setActivityState(prev => ({ ...prev, [field]: fieldValue }));
        // }
        // onUpdateActivity(field, fieldValue);

        // Updated: Use provided value if available (for paste scenarios), otherwise use state value
        const fieldValue = value !== null ? value : (activityState[field] !== undefined ? activityState[field] : '');
        // Update state with the value being saved - this ensures it's displayed immediately
        setActivityState(prev => ({ ...prev, [field]: fieldValue }));
        // Also update the ref
        if (field === 'description') {
            editorContentRef.current = fieldValue;
            setEditingDescription(fieldValue);
        }
        // Call the update function
        onUpdateActivity(field, fieldValue);
        // Only update attachments if there are pending ones
        if (localPendingAttachments && localPendingAttachments.length > 0) {
            onUpdateActivity('attachments', localPendingAttachments);
            // Clear local attachments after saving
            setLocalPendingAttachments([]);
        }
    };

    // Handlers for local attachment management
    const handleFilesAdded = (newFiles) => {
        setLocalPendingAttachments(prev => [...prev, ...newFiles]);
    };

    const handleFileRemoved = (fileId) => {
        setLocalPendingAttachments(prev => prev.filter(f => f.id !== fileId));
    };

    // Unique toolbar ID per activity to avoid conflicts
    const toolbarId = `custom-toolbar-task-${activity?.['_id'] || 'new'}`;

    setMentionMembers(currentWorkspace?.['members'] || []);
    const editorModules = {
        toolbar: `#${toolbarId}`,
        clipboard: {
            matchVisual: false,
            allowed: {
                tags: ['p', 'br', 'strong', 'em', 'u', 's', 'ul', 'ol', 'li', 'blockquote', 'a', 'img'],
                attributes: ['href', 'src', 'alt', 'style']
            }
        },
        mention: getMentionModule()
    };
    const formats = ['font', 'size', 'color', 'background', 'align', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'bullet', 'link', 'image', 'align', 'mention'];
    return (
        <>
            <AccordionItemHeading>
                <AccordionItemButton>
                    <div className='title-area style-sction'>
                        <p><span>{_.capitalize(activityState?.['taskType'])} Task assigned </span> to {activityData?.['assignedTo']}</p>
                        <div className='right-siede' onClick={(e) => e.stopPropagation()}>
                            {hasExternalData && (
                                <Tooltip title={`View ${dataSource || 'external'} data`}>
                                    <IconButton
                                        size="small"
                                        className="margin-right-sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onViewData();
                                        }}
                                    >
                                        <DataIcon fontSize="small" color="primary" />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Actions {...props} />
                            <p>
                                <i className="icon icon-gray">
                                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M3.66748 0.917969V2.29297" stroke="currentColor" strokeWidth="0.6875" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M7.33374 0.917969V2.29297" stroke="currentColor" strokeWidth="0.6875" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M1.60596 4.16748H9.39762" stroke="currentColor" strokeWidth="0.6875" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M9.62598 3.89714V7.79297C9.62598 9.16797 8.93848 10.0846 7.33431 10.0846H3.66764C2.06348 10.0846 1.37598 9.16797 1.37598 7.79297V3.89714C1.37598 2.52214 2.06348 1.60547 3.66764 1.60547H7.33431C8.93848 1.60547 9.62598 2.52214 9.62598 3.89714Z" stroke="currentColor" strokeWidth="0.6875" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M5.49902 6.2793H5.50422" stroke="currentColor" strokeWidth="1.03125" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M3.80273 6.2793H3.80792" stroke="currentColor" strokeWidth="1.03125" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M3.80273 7.6543H3.80792" stroke="currentColor" strokeWidth="1.03125" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </i>
                                Due: {activityData?.['dueDate']}
                            </p>
                        </div>
                    </div>
                    <div className={`title-area editing-title ${openState.isEditing ? 'is-editing' : ''}`}>
                        <div className="checkbox-left" onClick={(e) => e.stopPropagation()}>
                            <Checkbox icon={<CircleUnchecked />} checkedIcon={<CircleCheckedFilled />} checked={activityState?.['status'] == 'completed'} onClick={(e) => {
                                e.stopPropagation();
                                /* Mark Task as completed */
                                dispatch(completeActivity({ id: activityState['_id'] }));
                            }} />

                            {openState.isEditing ? (
                                <div className="edit-wrapper">
                                    <input
                                        autoFocus
                                        type="text"
                                        className="edit-input"
                                        value={activityState?.['title']}
                                        onClick={(e) => e.stopPropagation()}
                                        onBlur={() => updateActivity('title')}
                                        onChange={(e) => setActivityState({ ...activityState, title: e['target']['value'] })}
                                        onKeyDown={(e) => {
                                            e.stopPropagation();
                                            if (e['key'] === 'Enter') updateActivity('title');
                                            else if (e['key'] === 'Escape') toggleDropdownAction('isEditing');
                                        }}
                                    />
                                    <div className="edit-actions">
                                        <button className="btn-style-new" onClick={() => updateActivity('title')}>Save</button>
                                        <button className="btn-style-border" onClick={() => toggleDropdownAction('isEditing')}>Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <AccordionItemState>
                                    {({ expanded }) => (
                                        <span
                                            onClick={(e) => {
                                                // Only enter edit mode when the accordion is open — otherwise the
                                                // click should just expand the row (handled by the accordion button).
                                                if (!expanded) return;
                                                e.stopPropagation();
                                                toggleDropdownAction('isEditing');
                                            }}
                                            className={`title-text position-relative task-title-editable ${activityState?.['status'] === 'completed' ? 'strike' : ''}`}
                                        >
                                            {activityState?.['title'] || ''}
                                            <i className="edit-icon"><svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" focusable="false" data-icon-name="Edit" className="StyledIcon-sc-1jfhstb-0 iTgcdA"><path d="m11.86 27.84.25-.1h.1c.08-.05.16-.11.23-.17l12.15-12.08c.28-.28.44-.65.43-1.05-.02-.84-.71-1.5-1.55-1.48-.38 0-.75.16-1.03.43L11.43 24.3l-.88-.87 10.97-10.87c.5-.66.38-1.6-.28-2.11a1.5 1.5 0 0 0-1.82 0L8.41 21.32l-.89-.87L18.54 9.56c.53-.64.45-1.59-.19-2.12a1.51 1.51 0 0 0-1.94.01L4.32 19.38c-.12.13-.21.27-.28.43v.14s.01.08 0 .13l-2.02 7.98q-.03.18 0 .36c0 .82.68 1.49 1.5 1.48.12.02.25.02.38 0l8.07-1.98-.1-.09ZM25.93 3.06a3.527 3.527 0 0 0-4.9 0L19.42 4.6c-.28.28-.44.66-.44 1.06s.16.77.44 1.05l5.87 5.8c.59.59 1.55.59 2.14 0l1.56-1.54a3.416 3.416 0 0 0 0-4.84l-3.07-3.07Z"></path></svg></i>
                                        </span>
                                    )}
                                </AccordionItemState>
                            )}
                        </div>
                    </div>
                </AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel>
                <div className="accordion-content">
                    <div className="description-area">
                        <h5>Description</h5>
                        <div className={`description-text title-area editing-title ${openState.isEditingDescription ? 'is-editing' : ''}`}>
                            {openState.isEditingDescription ? (
                                <div className="edit-wrapper">
                                    <div className="editor-wrapper" onClick={(e) => e.stopPropagation()}>
                                        {/* Previous code for value prop - no default value: value={activityState['description']} */}
                                        {/* Previous code for onBlur - immediate save (caused race conditions with paste): onBlur={() => updateActivity('description')} */}
                                        {/* Previous code for onChange - direct mutation (caused issues with paste): onChange={(value) => setActivityState({ ...activityState, description: value })} */}
                                        <ReactQuill
                                            ref={quillRef}
                                            theme="snow" // must be snow for custom toolbar
                                            formats={formats}
                                            modules={editorModules}
                                            bounds=".editor-wrapper"
                                            placeholder="Add your not here..."
                                            value={editingDescription}
                                            /* REMOVED: onBlur auto-save - it was causing the editor to close and save empty content when pasting */
                                            /* onBlur={() => {
                                                // Small delay to ensure onChange has processed
                                                setTimeout(() => {
                                                    updateActivity('description');
                                                }, 100);
                                            }} */
                                            onKeyDown={(e) => {
                                                e.stopPropagation();
                                                if (e['key'] === 'Escape') toggleDropdownAction('isEditingDescription');
                                            }}
                                            onChange={(value) => {
                                                // Ensure we're updating the state with the new value
                                                // Use functional update to ensure we get the latest state
                                                const newValue = value || '';
                                                // Update ref immediately
                                                editorContentRef.current = newValue;
                                                // Update local editing state immediately (this updates the value prop)
                                                // This must be synchronous to prevent ReactQuill from resetting
                                                setEditingDescription(newValue);
                                                // Update main state
                                                setActivityState(prev => {
                                                    // Only update if the value actually changed to avoid unnecessary re-renders
                                                    if (prev.description !== newValue) {
                                                        return { ...prev, description: newValue };
                                                    }
                                                    return prev;
                                                });
                                            }}
                                        /* COMMENTED OUT: onPaste is not a standard ReactQuill prop */
                                        /* Using Quill's text-change event in useEffect instead */
                                        /* onPaste={(e) => {
                                            // Handle paste event to ensure content is captured
                                            // The onChange will be triggered after paste, but we ensure state is updated
                                            setTimeout(() => {
                                                if (quillRef.current) {
                                                    const quill = quillRef.current.getEditor();
                                                    const pastedContent = quill.root.innerHTML;
                                                    setActivityState(prev => ({ ...prev, description: pastedContent }));
                                                }
                                            }, 0);
                                        }} */
                                        />

                                        {/* Custom Toolbar - MUST BE visible */}
                                        <div id={toolbarId} className="bottom-toolbar">
                                            <span className="ql-formats">
                                                <button className="ql-bold" />
                                                <button className="ql-italic" />
                                                <button className="ql-underline" />
                                                <button className="ql-strike" />
                                            </span>
                                            <div className="ql-formats more-dropdown" ref={moreDropdownRef}>
                                                <button className="more-toggle" onClick={() => toggleDropdownAction('showMore')} >More ▾</button>
                                                <span
                                                    className={`more-dropdown-content ${openState.showMore ? 'show' : ''}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <svg data-test-id="popover-arrow" className="popover-arrow" fill="#FFFFFF" aria-hidden="true" width="30" height="28" viewBox="0 0 28 28"><path clipPath="url(#:ra5:)" fill="none" stroke="#CBD6E2" strokeWidth="3" d="M0,0 H28 L14,14 Q14,14 14,14 Z"></path><path stroke="#FFFFFF" d="M0,0 H28 L14,14 Q14,14 14,14 Z"></path><clipPath id=":ra5:"><rect x="-1" y="1" width="30" height="28"></rect></clipPath></svg>
                                                    <span className="ql-formats" onClick={(e) => e.stopPropagation()}>
                                                        <select className="ql-font" />
                                                        <select className="ql-size" />
                                                        <select className="ql-color" />
                                                        <select className="ql-background" />
                                                        <button className="ql-blockquote" />
                                                        <button className="ql-list" value="ordered" />
                                                        <select className="ql-align" />
                                                    </span>
                                                </span>
                                            </div>
                                            <span className="ql-formats">
                                                <button className="ql-list" value="ordered" />
                                                <button className="ql-list" value="bullet" />
                                            </span>
                                            <span className="ql-formats">
                                                <button className="ql-link" />
                                                <button className="ql-image" />
                                            </span>
                                            <div className="attchemnt-inner-ql">
                                                <AttachmentUploader
                                                    existingFiles={localPendingAttachments}
                                                    maxFiles={10}
                                                    useLocalState={true}
                                                    iconOnly={true}
                                                    onFilesAdded={handleFilesAdded}
                                                    onFileRemoved={handleFileRemoved}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <PendingAttachmentsList
                                        files={localPendingAttachments}
                                        onRemove={handleFileRemoved}
                                    />
                                    <div className="edit-actions">
                                        <button className="btn-style-new" onClick={() => {
                                            // Get content directly from Quill editor to ensure pasted content is captured
                                            if (quillRef.current) {
                                                const quill = quillRef.current.getEditor();
                                                // Get the HTML content directly from the editor (most reliable source)
                                                const editorContent = quill.root.innerHTML;
                                                // Update ref and state immediately before saving
                                                editorContentRef.current = editorContent;
                                                setEditingDescription(editorContent);
                                                setActivityState(prev => ({ ...prev, description: editorContent }));
                                                // Small delay to ensure state is updated, then save
                                                setTimeout(() => updateActivity('description', editorContent), 0);
                                            } else {
                                                // Fallback to ref or state if editor ref is not available
                                                const currentDescription = editorContentRef.current || activityState['description'] || '';
                                                setEditingDescription(currentDescription);
                                                updateActivity('description', currentDescription);
                                            }
                                        }}>Save</button>
                                        <button className="btn-style-border" onClick={() => toggleDropdownAction('isEditingDescription')}>Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <span className="title-text position-relative" onClick={(e) => {
                                    // Don't trigger edit mode if clicking on a link
                                    if (e.target.tagName === 'A' || e.target.closest('a')) return;
                                    toggleDropdownAction('isEditingDescription');
                                }}>
                                    <div className="description-text-container">
                                        <p
                                            className="description-text"
                                            dangerouslySetInnerHTML={{ __html: processHTMLForDisplay(activity?.description || activityState?.description || '') }}>
                                        </p>
                                    </div>
                                    <i className="edit-icon">
                                        <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" focusable="false" data-icon-name="Edit" className="StyledIcon-sc-1jfhstb-0 iTgcdA">
                                            <path d="m11.86 27.84.25-.1h.1c.08-.05.16-.11.23-.17l12.15-12.08c.28-.28.44-.65.43-1.05-.02-.84-.71-1.5-1.55-1.48-.38 0-.75.16-1.03.43L11.43 24.3l-.88-.87 10.97-10.87c.5-.66.38-1.6-.28-2.11a1.5 1.5 0 0 0-1.82 0L8.41 21.32l-.89-.87L18.54 9.56c.53-.64.45-1.59-.19-2.12a1.51 1.51 0 0 0-1.94.01L4.32 19.38c-.12.13-.21.27-.28.43v.14s.01.08 0 .13l-2.02 7.98q-.03.18 0 .36c0 .82.68 1.49 1.5 1.48.12.02.25.02.38 0l8.07-1.98-.1-.09ZM25.93 3.06a3.527 3.527 0 0 0-4.9 0L19.42 4.6c-.28.28-.44.66-.44 1.06s.16.77.44 1.05l5.87 5.8c.59.59 1.55.59 2.14 0l1.56-1.54a3.416 3.416 0 0 0 0-4.84l-3.07-3.07Z"></path>
                                        </svg>
                                    </i>
                                </span>
                            )}

                        </div>
                    </div>
                    {/* Attachments Section */}
                    {activity['attachments'] && activity['attachments'].length > 0 && (
                        <div className="attachments-area margin-top-md margin-bottom-md">
                            <h5>Attachments</h5>
                            <AttachmentList
                                enableActions={true}
                                showUploadedBy={false}
                                attachments={activity['attachments']}
                                onDelete={(attachmentId) => updateSinglePageReducer('attachments', (activity['attachments'] || []).filter(a => a['_id'] !== attachmentId))}
                            />
                        </div>
                    )}
                    <div className="assign-area">
                        <div className="assign-box">
                            <h5>Due date & time</h5>
                            <div className="date-picker-area">
                                <div className="date-picker-box">
                                    <i className="icon icon-gray">
                                        <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3.66748 0.917969V2.29297" stroke="currentColor" strokeWidth="0.6875" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M7.33374 0.917969V2.29297" stroke="currentColor" strokeWidth="0.6875" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M1.60596 4.16748H9.39762" stroke="currentColor" strokeWidth="0.6875" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M9.62598 3.89714V7.79297C9.62598 9.16797 8.93848 10.0846 7.33431 10.0846H3.66764C2.06348 10.0846 1.37598 9.16797 1.37598 7.79297V3.89714C1.37598 2.52214 2.06348 1.60547 3.66764 1.60547H7.33431C8.93848 1.60547 9.62598 2.52214 9.62598 3.89714Z" stroke="currentColor" strokeWidth="0.6875" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M5.49902 6.2793H5.50422" stroke="currentColor" strokeWidth="1.03125" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M3.80273 6.2793H3.80792" stroke="currentColor" strokeWidth="1.03125" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M3.80273 7.6543H3.80792" stroke="currentColor" strokeWidth="1.03125" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </i>
                                    {/* {activityState?.['dueDate'] ? moment(activityState?.['dueDate']).format('MM/DD/YYYY') : ''} */}
                                    <DatePicker
                                        selected={dueDate}
                                        dateFormat="MM/dd/yyyy"
                                        shouldCloseOnSelect={false}
                                        onChange={handleDateChange}
                                        open={openState['datePickerOpen']}
                                        calendarClassName="my-custom-calendar"
                                        popperClassName="my-datepicker-popper"
                                        className="single-page-date date-celander"
                                        onFocus={() => setOpenState((prev) => ({ ...prev, datePickerOpen: true }))}
                                        onClickOutside={() => setOpenState((prev) => ({ ...prev, datePickerOpen: false }))}
                                    />
                                </div>
                                <div className="date-picker-box">
                                    <i className="icon icon-dark-gray">
                                        <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M5.99976 1.14648C8.40439 1.14648 10.3541 3.09541 10.3542 5.5C10.3542 7.90474 8.4045 9.85449 5.99976 9.85449C3.59517 9.85432 1.64624 7.90463 1.64624 5.5C1.64642 3.09552 3.59527 1.14666 5.99976 1.14648Z" stroke="currentColor" strokeWidth="0.458333" />
                                            <path d="M7.70038 7.30155C7.64079 7.30155 7.58121 7.2878 7.52621 7.25113L6.10538 6.40322C5.75246 6.19238 5.49121 5.72947 5.49121 5.32155V3.44238C5.49121 3.25447 5.64704 3.09863 5.83496 3.09863C6.02288 3.09863 6.17871 3.25447 6.17871 3.44238V5.32155C6.17871 5.48655 6.31621 5.72947 6.45829 5.81197L7.87913 6.65988C8.04413 6.75613 8.09454 6.96697 7.99829 7.13197C7.92954 7.24197 7.81496 7.30155 7.70038 7.30155Z" fill="currentColor" />
                                        </svg>
                                    </i>
                                    {/* {activityState?.['dueDate'] ? moment(activityState?.['dueDate']).format('hh:mm A') : ''} */}

                                    <DatePicker
                                        showTimeSelect
                                        timeIntervals={15}
                                        selected={dueDate}
                                        showTimeSelectOnly
                                        dateFormat="hh:mm aa"
                                        open={openState.timePickerOpen}
                                        onClickOutside={() => setOpenState((prev) => ({ ...prev, timePickerOpen: false }))}
                                        onFocus={() => setOpenState((prev) => ({ ...prev, timePickerOpen: true }))}
                                        shouldCloseOnSelect={false}
                                        className="single-page-date"
                                        placeholderText="Select time"
                                        onChange={handleTimeChangeTime}
                                    />
                                </div>
                            </div>
                        </div>
                        {/* <div className="assign-box">
                            <h5>Reminder</h5>
                            <Dropdown isOpen={dropdowns['dropdownOpenReminderTask'] || false} toggle={() => toggleDropdownAction('dropdownOpenReminderTask')} >
                                <DropdownToggle caret color="secondary">
                                    At task due time
                                    <i className="icon">
                                        <svg width="5" height="4" viewBox="0 0 5 4" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3.74219 2.40453L2.38875 0L0.385372 0C0.0425479 0 -0.128864 0.552327 0.113969 0.876105L1.96379 3.34253C2.26019 3.73773 2.74229 3.73773 3.03868 3.34253L3.74219 2.40453Z" fill="var(--primary-color)" />
                                            <path d="M4.61365 0L2.38887 0L3.74231 2.40453L4.88863 0.876105C5.12789 0.552327 4.95648 0 4.61365 0Z" fill="var(--primary-color)" />
                                        </svg>
                                    </i>
                                </DropdownToggle>
                                <DropdownMenu>
                                    <DropdownItem>01/06/2025 9:38 AM</DropdownItem>
                                    <DropdownItem>01/06/2025 9:38 PM</DropdownItem>
                                    <DropdownItem>01/06/2025 9:38 AM</DropdownItem>
                                    <DropdownItem>01/06/2025 9:38 PM</DropdownItem>
                                </DropdownMenu>
                            </Dropdown>
                        </div> */}
                        <div className="assign-box">
                            <h5>Assigned to</h5>
                            <Dropdown isOpen={openState['dropdownOpenClient'] || false} toggle={() => toggleDropdownAction(`dropdownOpenClient`)} >
                                <DropdownToggle caret color="secondary">
                                    {activityState?.['assignedTo'] ? <>
                                        {activityState?.['assignedTo']?.['firstName'] ? activityState?.['assignedTo']?.['firstName'] + ' ' + activityState?.['assignedTo']?.['lastName'] : activityState?.['assignedTo']?.['email']}
                                    </> : 'Unassigned'}
                                    <i className="icon">
                                        <svg width="5" height="4" viewBox="0 0 5 4" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3.74219 2.40453L2.38875 0L0.385372 0C0.0425479 0 -0.128864 0.552327 0.113969 0.876105L1.96379 3.34253C2.26019 3.73773 2.74229 3.73773 3.03868 3.34253L3.74219 2.40453Z" fill="var(--primary-color)" />
                                            <path d="M4.61365 0L2.38887 0L3.74231 2.40453L4.88863 0.876105C5.12789 0.552327 4.95648 0 4.61365 0Z" fill="var(--primary-color)" />
                                        </svg>
                                    </i>
                                </DropdownToggle>
                                <DropdownMenu>
                                    {(currentWorkspace?.['members'] && currentWorkspace['members'].length > 0) && currentWorkspace['members'].map(member => {
                                        return <DropdownItem key={member['id']} onClick={() => onUpdateActivity('assignedTo', member)}>{member['firstName'] ? member['firstName'] + ' ' + member['lastName'] : member['email']}</DropdownItem>
                                    })}
                                </DropdownMenu>
                            </Dropdown>
                        </div>
                        <div className="assign-box">
                            <h5>Task Type</h5>
                            <Dropdown isOpen={openState['dropdownOpenTODO'] || false} toggle={() => toggleDropdownAction(`dropdownOpenTODO`)} >
                                <DropdownToggle caret color="secondary">
                                    {activityState?.['taskType'] ? activityState?.['taskType'].charAt(0).toUpperCase() + activityState?.['taskType'].slice(1) : ''}
                                    <i className="icon">
                                        <svg width="5" height="4" viewBox="0 0 5 4" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3.74219 2.40453L2.38875 0L0.385372 0C0.0425479 0 -0.128864 0.552327 0.113969 0.876105L1.96379 3.34253C2.26019 3.73773 2.74229 3.73773 3.03868 3.34253L3.74219 2.40453Z" fill="var(--primary-color)" />
                                            <path d="M4.61365 0L2.38887 0L3.74231 2.40453L4.88863 0.876105C5.12789 0.552327 4.95648 0 4.61365 0Z" fill="var(--primary-color)" />
                                        </svg>
                                    </i>
                                </DropdownToggle>
                                <DropdownMenu>
                                    {['call', 'email', 'to-do'].map((type, index) => (
                                        <DropdownItem className='text-capitalize' key={index} onClick={() => onUpdateActivity('taskType', type)}>{type.charAt(0).toUpperCase() + type.slice(1)}</DropdownItem>
                                    ))}
                                </DropdownMenu>
                            </Dropdown>
                        </div>
                        <div className="assign-box">
                            <h5>Priority</h5>
                            <Dropdown isOpen={openState['dropdownOpenPriority'] || false} toggle={() => toggleDropdownAction(`dropdownOpenPriority`)} >
                                <DropdownToggle caret color="secondary">
                                    {activityState?.['priority'] ? activityState?.['priority'].charAt(0).toUpperCase() + activityState?.['priority'].slice(1) : ''}
                                    <i className="icon">
                                        <svg width="5" height="4" viewBox="0 0 5 4" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3.74219 2.40453L2.38875 0L0.385372 0C0.0425479 0 -0.128864 0.552327 0.113969 0.876105L1.96379 3.34253C2.26019 3.73773 2.74229 3.73773 3.03868 3.34253L3.74219 2.40453Z" fill="var(--primary-color)" />
                                            <path d="M4.61365 0L2.38887 0L3.74231 2.40453L4.88863 0.876105C5.12789 0.552327 4.95648 0 4.61365 0Z" fill="var(--primary-color)" />
                                        </svg>
                                    </i>
                                </DropdownToggle>
                                <DropdownMenu>
                                    {['high', 'medium', 'low'].map((type, index) => (
                                        <DropdownItem className='text-capitalize' key={index} onClick={() => onUpdateActivity('priority', type)}>{type.charAt(0).toUpperCase() + type.slice(1)}</DropdownItem>
                                    ))}
                                </DropdownMenu>
                            </Dropdown>
                        </div>

                        {/* <div className="assign-box">
                            <h5>Queue</h5>
                            <Dropdown isOpen={dropdowns['dropdownOpenQueue'] || false} toggle={() => toggleDropdownAction(`dropdownOpenQueue`)} >
                                <DropdownToggle caret color="secondary">
                                    {activityState?.['queue'] ? activityState?.['queue'].charAt(0).toUpperCase() + activityState?.['queue'].slice(1) : ''}
                                    <i className="icon">
                                        <svg width="5" height="4" viewBox="0 0 5 4" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3.74219 2.40453L2.38875 0L0.385372 0C0.0425479 0 -0.128864 0.552327 0.113969 0.876105L1.96379 3.34253C2.26019 3.73773 2.74229 3.73773 3.03868 3.34253L3.74219 2.40453Z" fill="var(--primary-color)" />
                                            <path d="M4.61365 0L2.38887 0L3.74231 2.40453L4.88863 0.876105C5.12789 0.552327 4.95648 0 4.61365 0Z" fill="var(--primary-color)" />
                                        </svg>
                                    </i>
                                </DropdownToggle>
                                <DropdownMenu>
                                    {['none', 'active', 'follow-up', 'prospecting'].map((type, index) => (
                                        <DropdownItem className='text-capitalize' key={index}>{type.charAt(0).toUpperCase() + type.slice(1)}</DropdownItem>
                                    ))}
                                </DropdownMenu>
                            </Dropdown>
                        </div> */}
                    </div>
                </div>
            </AccordionItemPanel>
        </>
    );
};

export default Task;